/**
 * Delete User Dialog Component
 * Confirms user deletion (soft-delete)
 *
 * @module Features/UserManagement/Dialogs
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Stack,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useDeleteUserMutation, useGetUserByIdQuery } from '../Service/usersApi';
import { closeDeleteDialog } from '../slices/userManagementSlice';
import { useSnackbar } from 'notistack';

interface DeleteUserDialogProps {
  userId: number;
}

/**
 * Delete User Dialog Component
 */
const DeleteUserDialog: React.FC<DeleteUserDialogProps> = ({ userId }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { data: user, isLoading: isLoadingUser } = useGetUserByIdQuery(userId);
  const [deleteUser, { isLoading }] = useDeleteUserMutation();

  const onDelete = async () => {
    try {
      await deleteUser(userId).unwrap();

      enqueueSnackbar('Użytkownik dezaktywowany pomyślnie', { variant: 'success' });
      console.log(`✅ User ${user?.email} has been deactivated`);
      handleClose();
    } catch (error: any) {
      const message = error?.data?.message || 'Nie udało się dezaktywować użytkownika';
      enqueueSnackbar(message, { variant: 'error' });
      console.error('❌ Error:', message);
    }
  };

  const handleClose = () => {
    dispatch(closeDeleteDialog());
  };

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={handleClose}>
      <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>
        Dezaktywuj użytkownika
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Stack spacing={2}>
          {isLoadingUser ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={40} />
            </div>
          ) : (
            <>
              <Typography variant="body2">
                Zamierzasz dezaktywować następującego użytkownika:
              </Typography>

              <Alert severity="info">
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  Email: {user?.email}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 1 }}>
                  Imię i nazwisko: {user?.firstName} {user?.lastName}
                </Typography>
              </Alert>

              <Alert severity="warning">
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  ⚠️ Usunięcie miękkie
                </Typography>
                <Typography variant="body2">
                  • Użytkownik będzie dezaktywowany (IsActive = false)
                </Typography>
                <Typography variant="body2">
                  • Dane są zachowywane w bazie danych do celów audytu
                </Typography>
                <Typography variant="body2">• Użytkownik nie może się już logować</Typography>
                <Typography variant="body2">
                  • Możesz reaktywować użytkownika później poprzez jego edycję
                </Typography>
              </Alert>

              <Typography variant="body2" color="textSecondary">
                Ta akcja nie może być cofnięta. Czy na pewno chcesz dezaktywować tego użytkownika?
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Anuluj
        </Button>
        <Button
          onClick={onDelete}
          variant="contained"
          color="error"
          disabled={isLoading || isLoadingUser}
          startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
        >
          {isLoading ? 'Dezaktywowanie...' : 'Dezaktywuj użytkownika'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteUserDialog;
